import { motion } from "framer-motion";
import { ArrowUpRight, TrendingUp } from "lucide-react";
import AnimatedCounter from "./AnimatedCounter";

function formatIdr(value) {
  if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(2)}B`;
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return String(Math.round(value));
}

export const HeroMetric = ({ stats, tradingConfig, dailyState }) => {
  if (!stats) {
    return (
      <div className="panel p-6 lg:col-span-5 text-text-secondary font-mono text-sm" data-testid="hero-metric-panel">
        No daily state or trading config in Supabase. Run supabase/seed.sql.
      </div>
    );
  }

  const gainPositive = stats.gain >= 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="panel p-5 sm:p-6 flex flex-col gap-4 lg:col-span-5 relative overflow-hidden"
      data-testid="hero-metric-panel"
    >
      <Crosshair className="top-2 left-2" />
      <Crosshair className="top-2 right-2 rotate-90" />
      <Crosshair className="bottom-2 left-2 -rotate-90" />
      <Crosshair className="bottom-2 right-2 rotate-180" />

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="label-uppercase">Daily P&amp;L (IDR)</span>
          <span className="text-[10px] text-text-secondary">/ PUSHNOW</span>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="label-micro px-1.5 py-0.5 border border-border" style={{ borderRadius: 2 }}>
            {stats.regime}
          </span>
          <span className="label-micro px-1.5 py-0.5 border border-border" style={{ borderRadius: 2 }}>
            {stats.status}
          </span>
          <span className="label-micro px-1.5 py-0.5 border border-border" style={{ borderRadius: 2 }}>
            Deployed {stats.deployedPct}%
          </span>
        </div>
      </div>

      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <h1
            data-testid="hero-pnl-value"
            className={`font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-none tracking-tighter ${
              gainPositive ? "text-positive" : "text-magenta"
            }`}
          >
            Rp
            <AnimatedCounter value={stats.pnl} duration={1600} />
          </h1>
          <div className="flex items-center gap-2 mt-3">
            <ArrowUpRight className={`w-4 h-4 ${gainPositive ? "text-positive" : "text-magenta"}`} />
            <span className={`text-sm font-mono num ${gainPositive ? "text-positive" : "text-magenta"}`}>
              {gainPositive ? "+" : ""}
              <AnimatedCounter value={stats.gain} decimals={2} duration={1400} />% daily
            </span>
            <span className="ml-2 text-[10px] uppercase tracking-[0.2em] text-text-secondary">
              Capital Rp {formatIdr(stats.capitalIdr)}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-x-6 gap-y-2 text-right min-w-[260px]">
          <MiniStat label="Min score" value={tradingConfig?.min_composite_score_to_enter ?? stats.confidence} suffix="" />
          <MiniStat label="Target %" value={stats.dailyTargetPct} suffix="%" tone="positive" />
          <MiniStat label="Max loss" value={stats.maxDailyLossPct} suffix="%" tone="magenta" />
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-border pt-3 mt-1 flex-wrap gap-2">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-text-secondary">
          <TrendingUp className="w-3 h-3" />
          {stats.marketBias} · Trade date {stats.tradeDate}
        </div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-text-secondary">
          Live from Supabase
        </div>
      </div>
    </motion.div>
  );
};

const MiniStat = ({ label, value, suffix = "", tone = "default" }) => (
  <div>
    <div className="label-micro">{label}</div>
    <div
      className={`text-lg font-mono num ${
        tone === "magenta" ? "text-magenta" : tone === "positive" ? "text-positive" : "text-foreground"
      }`}
    >
      <AnimatedCounter value={Number(value) || 0} decimals={1} duration={1200} suffix={suffix} />
    </div>
  </div>
);

const Crosshair = ({ className = "" }) => (
  <svg
    className={`absolute w-3 h-3 text-text-secondary opacity-50 ${className}`}
    viewBox="0 0 12 12"
    fill="none"
  >
    <path d="M0 0 H6 M0 0 V6" stroke="currentColor" strokeWidth="1" />
  </svg>
);

export default HeroMetric;
